import { Box, Grid } from '@mui/material';
import FhirOrganizationInformation from './FhirOrganizationInformation';
import FhirOrganizationContacts from './FhirOrganizationContacts';
import { useSnackbar } from 'notistack';
import produce from 'immer';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import ContactDetails from 'src/careflow/care-coordination/patient/contact-detail/ContactDetails';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { EditableCard } from 'src/careflow/common/EditableCard';
import { NewAddressDetails } from 'src/careflow/care-coordination/patient/address-detail/NewAddressDetails';
import { IdentifierDetails } from 'src/careflow/care-coordination/patient/identifier-detail/IdentifierDetails';

const FhirGeneralInformation = ({
  organization,
  update,
  refresh,
  translate,
}: {
  organization: {
    main: WrappedOrganization | null;
    partOf: WrappedOrganization | null;
  };
  update: Function;
  refresh: Function;
  translate: any;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [organizationIdentifierTypesVS] = useValueSet('ph-organization-identifier-types', {
    map: ValueSetWrapper,
    autofetch: !!organization,
  });

  const handleOrgInfo = async (data) => {
    try {
      await update(data);
      enqueueSnackbar(translate('dialogs.updated'));
    } catch (error) {
      enqueueSnackbar(translate('dialogs.notUpdated'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container sx={{ width: '100%' }} spacing={2}>
        <Grid item xs={6}>
          <FhirOrganizationInformation
            organization={organization}
            updateOrganization={(data) =>
              handleOrgInfo(
                produce(organization?.main!, (draft) => {
                  draft.name = data.name;
                  draft.type = data.type;
                  draft.alias = data.alias;
                  draft.active = data.active;
                })
              )
            }
            refresh={refresh}
            translate={translate}
          />
        </Grid>
        <Grid item xs={6}>
          <ContactDetails
            telecom={organization?.main?.telecom}
            update={(data) =>
              handleOrgInfo(
                produce(organization?.main!, (draft) => {
                  draft.telecom = [...data];
                })
              )
            }
            title={translate('details.general.contactTitle')}
            permissions={{
              isAllowedToAdd: checkAclValidation({
                acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACT_DETAILS.ADD],
              }),
              isAllowedToEdit: checkAclValidation({
                acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACT_DETAILS.EDIT],
              }),
              isAllowedToDelete: checkAclValidation({
                acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACT_DETAILS.DELETE],
              }),
            }}
            valueSetsIds={['organization-contact-use-email', 'organization-contact-use-phone']}
          />
        </Grid>
        <Grid item xs={12}>
          <EditableCard title={translate('details.general.addressTitle')} showEditButton={false}>
            <NewAddressDetails
              addressess={organization?.main?.address ?? []}
              onAddressesSave={(data) =>
                handleOrgInfo(
                  produce(organization?.main!, (draft) => {
                    draft.address = data;
                  })
                )
              }
              permissions={{
                isAllowedToAdd: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.ADDRESS_DETAILS.ADD],
                }),
                isAllowedToEdit: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.ADDRESS_DETAILS.EDIT],
                }),
                isAllowedToDelete: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.ADDRESS_DETAILS.DELETE],
                }),
                isAllowedAddressHousingStatus: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.ADDRESS_DETAILS.ADD_HOUSING_STATUS],
                }),
              }}
              config={null}
              use="work"
            />
          </EditableCard>
        </Grid>
        <Grid item xs={12}>
          <EditableCard
            title={translate('details.general.identifiersTitle')}
            showEditButton={false}
          >
            <IdentifierDetails
              identifiers={organization?.main?.identifier ?? []}
              catalog={organizationIdentifierTypesVS?.asListAll?.() ?? []}
              systemVS={organizationIdentifierTypesVS?.getConceptSystem()}
              onUpdateIdentifiers={(data) =>
                update(
                  produce(organization?.main!, (draft) => {
                    draft.identifier = data;
                  })
                )
              }
              permissions={{
                isAllowedToAdd: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.ADD],
                }),
                isAllowedToEdit: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.EDIT],
                }),
                isAllowedToDelete: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.DELETE],
                }),
                isAllowedToViewHidden: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.HIDDEN.VIEW],
                }),
                isAllowedToEditHidden: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.HIDDEN.EDIT],
                }),
                isAllowedToDeleteHidden: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.HIDDEN.DELETE],
                }),
                isAllowedToEditExternal: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.EXTERNAL.EDIT],
                }),
                isAllowedToDeleteExternal: checkAclValidation({
                  acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.IDENTIFIER_DETAILS.EXTERNAL.DELETE],
                }),
              }}
            />
          </EditableCard>
        </Grid>
        <Grid item xs={12}>
          <EditableCard title={translate('details.general.contacts')} showEditButton={false}>
            <FhirOrganizationContacts
              organization={organization?.main}
              update={update}
              translate={translate}
            />
          </EditableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FhirGeneralInformation;
